import { useAuth } from "../contexts/AuthContext";
import { GrainLoader } from "@/components/ui/GrainLoader";
import { Navigate, useNavigate, useLocation } from "react-router-dom";
import { WarehouseScreen } from "./WarehouseScreen";
import { useEffect, useState } from "react";

import RbacFallback from "./rbac/RbacFallback";
import { BootShellSkeleton } from "./BootShellSkeleton";
import {
  hasBootContentPainted,
  subscribeBootContentPainted,
} from "@/helpers/bootHandoff";
import { ROUTES } from "@/constants";


export function ProtectedRoute({ children, isPublic = false }: any) {
  // @ts-ignore
  const navigate = useNavigate();
  const location = useLocation();
  const {
    currentUser,
    userDetails,
    loading,
    warehouse,
    warehouseLoading,
    roleAccess,
    roleAccessLoading
  } = useAuth();
  
  const isSlackRedirect = Boolean(
    new URLSearchParams(location.search).get("slack") ||
    new URLSearchParams(location.search).get("pulse")
  );

  useEffect(() => {
    if (!userDetails || !currentUser) {
      if (isSlackRedirect) {
        navigate(`${ROUTES.SLACK_CONNECT_STATUS}${location.search}`, { replace: true });
        return;
      }
      const url = new URL(window.location.href);
      const redirectTo = window.location.pathname.startsWith("/auth") ? "" : window.location.pathname;
      let search = `${url.search.replace('?', '')}${redirectTo ? `&redirectTo=${redirectTo}` : ''}`;
      search = search.startsWith("&") ? search.substring(1) : search;
      navigate(`/auth?${search}`);
    }
  }, [userDetails, currentUser, isSlackRedirect, location.search, navigate]);

  // Admin/Settings gate exemption. The pre-shell App.tsx deliberately routed
  // /admin/* and /settings/* through RoleProtectedRoute directly, NOT
  // ProtectedRoute, so internal admins are never bounced through the warehouse /
  // APP_PERMISSION gate (an admin with no connected warehouse, or without
  // APP_PERMISSION, must still reach /admin and /settings). PR #893 collapsed all
  // routes into `/*` → ProtectedRoute, which re-introduced that gate here; this
  // restores the bypass. Authorization is NOT lost: the /settings and /admin inner
  // routes in Threads.tsx carry their own per-section role guards.
  const { pathname } = location;
  const isGateExempt =
    pathname === "/admin" ||
    pathname.startsWith("/admin/") ||
    pathname === "/settings" ||
    pathname.startsWith("/settings/");

  if (isGateExempt) {
    // Keep the normal auth behavior (the auth-redirect effect above + these
    // checks) but SKIP the warehouse spinner, WarehouseScreen gate, and the
    // APP_PERMISSION RoleProtectedRoute wrapper — render children directly so the
    // section's own role guards decide access.
    if (loading) {
      return (
        <div className="flex justify-center items-center h-[100vh] w-[100vw] absolute">
          <GrainLoader />
        </div>
      );
    }
    if (!currentUser || isPublic) {
      if (isSlackRedirect) {
        return <Navigate to={`${ROUTES.SLACK_CONNECT_STATUS}${location.search}`} replace />;
      }
      return <Navigate to="/auth" />;
    }
    return <>{children}</>;
  }

  // Boot LCP Phase 2: once auth is resolved, the in-flight warehouse call paints
  // the optimistic shell (real greeting text = LCP element) instead of a
  // full-screen spinner. Before auth resolves there is no shell to promise yet —
  // and the redirect effect above may be about to bounce to /auth — so that case
  // keeps the loader.
  if (warehouseLoading) {
    if (currentUser && userDetails && !isPublic) {
      return <BootShellSkeleton />;
    }
    return (
      <div className="flex flex-col justify-center items-center h-[100vh] w-[100vw] absolute bg-tint-secondary">
        <div>
          <GrainLoader />
        </div>
        <div className="text-text-medium-emphasis mt-8">
          Connecting pulse backend...
        </div>
      </div>
    );
  } else if (warehouse && !warehouse?.length && !roleAccessLoading && roleAccess?.APP_PERMISSION) {
    // The warehouse query no longer waits for APP_PERMISSION (it runs in parallel
    // with RBAC now), so an empty warehouse can land BEFORE role access resolves —
    // or for a user who has no access at all. Only offer the connect screen once
    // access is resolved AND granted; everything else falls through to the
    // RoleProtectedRoute branch below, which is the denial path (RbacFallback).
    return <WarehouseScreen />;
  } else {
    if (loading) {
      return (
        <div className="flex justify-center items-center h-[100vh] w-[100vw] absolute">
          <GrainLoader />
        </div>
      );
    }

    if (!currentUser || isPublic) {
      if (isSlackRedirect) {
        return <Navigate to={`${ROUTES.SLACK_CONNECT_STATUS}${location.search}`} replace />;
      }
      return <Navigate to="/auth" />;
    }

    if (currentUser && !isPublic) {
      return <RoleProtectedRoute hasAccess={roleAccess?.APP_PERMISSION || false} isLoading={roleAccessLoading || false} fallback={<RbacFallback/>}>
        {children}
      </RoleProtectedRoute>
    }
  }
  return <RoleProtectedRoute hasAccess={roleAccess?.APP_PERMISSION || false} isLoading={roleAccessLoading || false} fallback={<RbacFallback/>}>
    {children}
  </RoleProtectedRoute>
}

/**
 * How long the boot overlay may outlive the access resolution before it is torn
 * down regardless. The hand-off signal comes from a component mount, so a lazy
 * chunk that never loads (or a route that renders neither `Suggestions` nor
 * `ThreadsInner`) must not be able to wedge a full-screen skeleton over the app.
 */
const BOOT_OVERLAY_TIMEOUT_MS = 8000;

export function RoleProtectedRoute({children, hasAccess, isLoading=false, fallback}: {children: React.ReactNode, hasAccess: boolean, isLoading?: boolean, fallback?: React.ReactNode}) {

  const { currentUser, userDetails } = useAuth();
  const navigate = useNavigate();

  // Boot LCP P1 — the hand-off. Access resolving is NOT the same as real content
  // being on screen: children = lazy `Threads` → openui `ChatProvider` → `Chat`,
  // and the real greeting used to arrive 1-2s later. Swapping the skeleton out at
  // access-resolution time therefore removed the LCP element from the DOM and put
  // it back again, which is exactly what the skeleton existed to prevent.
  //
  // So: render the children immediately (they need to mount to make progress) and
  // keep the skeleton on top as an overlay until `signalBootContentPainted()`
  // fires. The latch inside `bootHandoff` is session-lifetime, so a later route
  // change starts with `hasBootContentPainted() === true` and never overlays.
  const [showBootOverlay, setShowBootOverlay] = useState(() => !hasBootContentPainted());

  useEffect(() => {
    if (!showBootOverlay) return;
    const unsubscribe = subscribeBootContentPainted(() => setShowBootOverlay(false));
    const timeout = setTimeout(() => setShowBootOverlay(false), BOOT_OVERLAY_TIMEOUT_MS);
    return () => {
      unsubscribe();
      clearTimeout(timeout);
    };
  }, [showBootOverlay]);

  useEffect(() => {
    if (!userDetails || !currentUser) {
      const url = new URL(window.location.href);
      const redirectTo = window.location.pathname.startsWith("/auth") ? "" : window.location.pathname;
      let search = `${url.search.replace('?', '')}${redirectTo ? `&redirectTo=${redirectTo}` : ''}`;
      search = search.startsWith("&") ? search.substring(1) : search;
      navigate(`/auth?${search}`);
    }
  }, [userDetails, currentUser]);

  return (
    <div className="relative h-screen w-full">
      <div className="h-full w-full">
        {
          isLoading
          ? (
            // Boot LCP Phase 2: role access is the last leg of the boot chain and
            // this component is only ever mounted on the boot path (both call
            // sites are in ProtectedRoute above), so the wait paints the
            // optimistic shell rather than a spinner. Denial still swaps to the
            // fallback below the moment access resolves.
            currentUser && userDetails
              ? <BootShellSkeleton />
              : <div className="flex items-center justify-center h-full w-full">
                  <GrainLoader />
                </div>
          )
          : !currentUser ? (
              <div className="flex items-center justify-center h-full w-full">
                <div className="text-[22px]">
                  Please login to access this page
                </div>
              </div>
            ) : (
              <>
                {hasAccess ? (
                  children
                ) : fallback ? (
                  fallback
                ) : (
                  <div className="flex items-center justify-center h-full w-full">
                    <div className="text-[22px]">
                      You dont have access to this page
                    </div>
                  </div>
                )}
              </>
            )
        }

      </div>
      {/* The boot hand-off overlay. Only ever on the GRANTED path: a denial has
          nothing to hand off to, so `RbacFallback` must show immediately. It sits
          above the children but below the toast layer (which is a sibling of the
          router in App.tsx), and it is inert to input so nothing is swallowed. */}
      {showBootOverlay && !isLoading && currentUser && hasAccess && (
        <div className="absolute inset-0 z-30 pointer-events-none">
          <BootShellSkeleton />
        </div>
      )}
      {/* The bottom-left user-avatar card that used to live here is retired: the
          one persistent shell (Threads → AppRail) now owns the footer avatar +
          theme toggle. Keeping a second UserAvatar here stacked a duplicate,
          hidden "Toggle theme" button directly over the rail's, so coordinate
          clicks could land on the no-op copy (F24). AppRail is the single owner. */}
    </div>
  )
}
